import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { Heading } from "react-aria-components";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { formatTournamentDivision } from "@/components/base/form/fields/division-picker";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { addToast } from "@/components/base/toast";
import {
  useActiveDivisionId,
  usePoolsQueryOptions,
  useTeamsQueryOptions,
} from "@/components/tournaments/context";
import type { Gender } from "@/db/schema/shared";
import { useTRPC } from "@/integrations/trpc/react";

export type TransferToTournamentFormProps = {
  tournamentDivisionTeamId: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function TransferToTournamentForm({
  tournamentDivisionTeamId,
  ...props
}: TransferToTournamentFormProps) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const tournamentDivisionId = useActiveDivisionId();
  const teamQueryOptions = useTeamsQueryOptions();
  const poolsQueryOptions = usePoolsQueryOptions();
  const withdrawnTeamsQueryOptions =
    trpc.tournaments.getWithdrawnTeams.queryOptions({ tournamentDivisionId });

  const { data: teamInfo } = useQuery({
    ...trpc.tournaments.getTeamForSwap.queryOptions({
      tournamentDivisionTeamId,
    }),
    enabled: props.isOpen,
  });

  const { mutate, failureReason } = useMutation({
    ...trpc.tournaments.tdTransferTeam.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(teamQueryOptions);
      queryClient.invalidateQueries(poolsQueryOptions);
      queryClient.invalidateQueries(withdrawnTeamsQueryOptions);

      addToast({
        variant: "success",
        title: "Team transferred",
        description:
          "The team has been transferred to the selected tournament.",
      });

      props.onOpenChange(false);
    },
  });

  const [targetDisplay, setTargetDisplay] = useState<string | null>(null);

  // Look up the selected division from the DivisionPicker's cached search results
  const resolveTargetDisplay = useCallback(
    (targetId: number) => {
      if (!targetId) {
        setTargetDisplay(null);
        return;
      }

      const queries = queryClient.getQueriesData<{
        data: Array<{
          id: number;
          gender: Gender;
          name: string | null;
          division: {
            display: string | null;
            name: string;
            maxAge: number | null;
          };
          tournament: {
            date: string;
            venue: { name: string; city: string };
          };
        }>;
      }>(trpc.tournaments.searchDivisions.pathFilter());

      for (const [, data] of queries) {
        const match = data?.data?.find((td) => td.id === targetId);
        if (match) {
          setTargetDisplay(formatTournamentDivision(match));
          return;
        }
      }

      setTargetDisplay(null);
    },
    [queryClient, trpc],
  );

  const form = useAppForm({
    defaultValues: {
      targetTournamentDivisionId: 0 as number,
    },
    onSubmit: ({ value }) => {
      if (!value.targetTournamentDivisionId) return;

      mutate({
        tournamentDivisionTeamId,
        targetTournamentDivisionId: value.targetTournamentDivisionId,
      });
    },
  });

  return (
    <Modal {...props}>
      <form
        className="flex flex-col space-y-4 p-3 md:p-6"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <Heading className={title({ size: "sm" })} slot="title">
          Transfer to Another Tournament
        </Heading>

        {failureReason && (
          <form.AppForm>
            <form.Alert
              title="Unable to transfer team"
              description={failureReason.message}
            />
          </form.AppForm>
        )}

        <p className="text-sm text-gray-700">
          Select a tournament division to transfer this team to.
        </p>

        <form.AppField
          name="targetTournamentDivisionId"
          listeners={{
            onChange: ({ value }) => {
              resolveTargetDisplay(value);
            },
          }}
          children={(field) => (
            <field.DivisionPicker
              label="Target Division"
              field={field}
              selectedDivisionIds={
                teamInfo ? [teamInfo.tournamentDivisionId] : []
              }
              dateRange={
                teamInfo ? { startDate: teamInfo.tournamentDate } : undefined
              }
              // Junior division orders sit above every adult level, so this
              // only narrows the adult divisions to those the team's rating
              // allows. Juniors teams can also move to rated (adult)
              // divisions; adult teams only see adult divisions. The server
              // still validates every player against the target division.
              minDivisionOrder={teamInfo?.maxLevelOrder}
              isJunior={teamInfo?.isJunior === false ? false : undefined}
              teamSize={teamInfo?.teamSize}
              gender={
                teamInfo?.gender === "unknown" ? undefined : teamInfo?.gender
              }
            />
          )}
        />

        <form.AppForm>
          <form.Footer>
            <Button onPress={() => props.onOpenChange(false)}>Cancel</Button>

            <form.Subscribe
              selector={(state) => state.values.targetTournamentDivisionId}
              children={(targetId) => (
                <form.ConfirmSubmitButton
                  requireChange={false}
                  isDisabled={!targetId}
                  description={
                    <p className="text-sm text-gray-700">
                      Transfer{" "}
                      <span className="text-foreground font-medium">
                        {teamInfo?.playerNames.join(" / ") ?? "this team"}
                      </span>{" "}
                      to{" "}
                      <span className="text-foreground font-medium">
                        {targetDisplay ?? "the selected division"}
                      </span>
                      ? This will remove them from the current tournament.
                    </p>
                  }
                >
                  Transfer
                </form.ConfirmSubmitButton>
              )}
            />
          </form.Footer>
        </form.AppForm>
      </form>
    </Modal>
  );
}
